import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import { useGetUpcomingDrivesForJobseeker } from 'src/hooks/drives.hooks';
import DriveCard from './DriveCard';
import { DRIVE_FONT_STYLE, useDriveFont } from './shared';

const SCROLL_STEP = 400;

const Heading = ({ count }: { count: number }) => (
	<h3 className="text-[16px] tsm:text-[18px] font-[600] text-[#1F3343] dark:text-[#CDF]">
		Upcoming placement drives <span className="text-[#8A8BA3] font-[500] whitespace-nowrap">({count})</span>
	</h3>
);

// 'sidebar': a vertical list in the right-hand column that scrolls inside itself once there are many drives.
// 'row': one horizontally scrollable row for small screens, so it never pushes the job list far down.
const UpcomingDrives = ({ variant }: { variant: 'row' | 'sidebar' }) => {
	useDriveFont();
	const { data } = useGetUpcomingDrivesForJobseeker();
	const scrollerRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const updateArrows = useCallback(() => {
		const el = scrollerRef.current;
		if (!el) return;
		setCanScrollLeft(el.scrollLeft > 4);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
	}, []);

	useEffect(() => {
		updateArrows();
		window.addEventListener('resize', updateArrows);
		return () => window.removeEventListener('resize', updateArrows);
	}, [data, updateArrows]);

	if (!data?.drives?.length) return null;
	const { drives } = data;

	if (variant === 'sidebar') {
		return (
			<aside className="hidden md:block md:sticky md:top-4 mt-[16px]" style={DRIVE_FONT_STYLE}>
				<Heading count={drives.length} />
				<div className="-mx-3 mt-0 max-h-[calc(100vh-160px)] overflow-y-auto px-3 pt-3 pb-6 space-y-4 [scrollbar-width:thin]">
					{drives.map(drive => (
						<DriveCard key={drive._id} drive={drive} className="w-full" />
					))}
				</div>
			</aside>
		);
	}

	const scrollBy = (offset: number) => scrollerRef.current?.scrollBy({ left: offset, behavior: 'smooth' });
	const arrowClass =
		'w-8 h-8 tsm:w-9 tsm:h-9 shrink-0 rounded-full border border-[#E6E6EE] bg-white text-[#3C3E55] flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed';

	return (
		<section className="mt-4 mb-2" style={DRIVE_FONT_STYLE}>
			<div className="flex items-center justify-between gap-3">
				<Heading count={drives.length} />
				{canScrollLeft || canScrollRight ? (
					<div className="flex gap-2">
						<button aria-label="Previous drives" className={arrowClass} disabled={!canScrollLeft} onClick={() => scrollBy(-SCROLL_STEP)}>
							<HiChevronLeft />
						</button>
						<button aria-label="Next drives" className={arrowClass} disabled={!canScrollRight} onClick={() => scrollBy(SCROLL_STEP)}>
							<HiChevronRight />
						</button>
					</div>
				) : null}
			</div>
			<div
				ref={scrollerRef}
				onScroll={updateArrows}
				className="-mx-3 mt-0 flex gap-4 overflow-x-auto snap-x snap-mandatory px-3 pt-3 pb-6 scroll-px-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
			>
				{drives.map(drive => (
					<div key={drive._id} className={`snap-start shrink-0 ${drives.length > 1 ? 'w-[calc(100%-32px)] tsm:w-[calc(100%-24px)]' : 'w-full'}`}>
						<DriveCard drive={drive} className="w-full" />
					</div>
				))}
			</div>
		</section>
	);
};

export default UpcomingDrives;
